import React, { useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import logoImage from "../assets/1.gif";
import webLogo from "../assets/logo.png";
// Store badges from src/assets (lokalizované soubory SVG)
import gpCs from "../assets/android/cs.svg";
import gpDe from "../assets/android/de.svg";
import gpEn from "../assets/android/en.svg";
import gpEs from "../assets/android/es.svg";
import iosCsBlack from "../assets/ios/cs_black.svg";
import iosDeBlack from "../assets/ios/de_black.svg";
import iosEnBlack from "../assets/ios/en_black.svg";
import iosEsBlack from "../assets/ios/es_black.svg";

interface HomeProps {
   scrollToServices: () => void;
   scrollToContactUs: () => void;
}

const Home: React.FC<HomeProps> = ({ scrollToServices }) => {
   const { t, i18n } = useTranslation();

   // Locale mapping for store badges
   const rawLang = (i18n.language || "en").toLowerCase();
   const lang = ["cs", "de", "en", "es"].includes(rawLang.slice(0, 2))
      ? (rawLang.slice(0, 2) as "cs" | "de" | "en" | "es")
      : ("en" as const);

   // Mapy na importované SVG (CRA je zabalí a vrátí URL string)
   const googleBadges = { cs: gpCs, de: gpDe, en: gpEn, es: gpEs } as const;
   const appleBadges = { cs: iosCsBlack, de: iosDeBlack, en: iosEnBlack, es: iosEsBlack } as const;

   const googleBadgeSrc = googleBadges[lang];
   const appleBadgeSrc = appleBadges[lang];

   // Jednoduchý fallback: při chybě načtení přepnout na EN verzi
   const onGoogleBadgeError: React.ReactEventHandler<HTMLImageElement> = (e) => {
      const img = e.currentTarget;
      if (!img.dataset.fallback) {
         img.dataset.fallback = "1";
         img.src = googleBadges.en;
         console.warn("Google badge fallback → EN", { failed: googleBadgeSrc });
         // Po přepnutí na EN znovu přeměřit
         // no-op: v tomto layoutu nic nepřeměřujeme
         }
      };

   const onAppleBadgeError: React.ReactEventHandler<HTMLImageElement> = (e) => {
      const img = e.currentTarget;
      if (!img.dataset.fallback) {
         img.dataset.fallback = "1";
         img.src = appleBadges.en;
         console.warn("Apple badge fallback → EN", { failed: appleBadgeSrc });
      }
  };

  // Měření šířky řádku s badge a nastavení stejné šířky pro .store-group (open web tlačítko bude 100% této šířky)
  const groupRef = useRef<HTMLDivElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!groupRef.current || !rowRef.current) return;

    // Capture current elements so cleanup doesn't use possibly changed refs
    const groupEl = groupRef.current;
    const rowEl = rowRef.current;

    const measure = () => {
      if (groupEl && rowEl) {
        const w = Math.ceil(rowEl.getBoundingClientRect().width);
        if (w > 0) {
          groupEl.style.width = `${w}px`;
        }
      }
    };

    // Observer pro změny velikosti (např. změna jazyka, wrap na malých šířkách)
    const RO: any = (window as any).ResizeObserver;
    const ro = RO ? new RO(() => measure()) : null;
    if (ro && rowEl) ro.observe(rowEl);

    // Přepočet na resize okna
    window.addEventListener("resize", measure);

    // prvotní měření (po dalším tiknutí kvůli načtení obrázků)
    const t = setTimeout(measure, 0);

    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      if (ro && rowEl) ro.unobserve(rowEl);
    };
  }, [lang]);

   return (
      <motion.div
         initial={{ opacity: 0, y: -50 }}
         animate={{ opacity: 1, y: 0 }}
         transition={{ duration: 0.8 }}
      >
         <Container fluid className='hero-section p-0 m-0'>
            <Row className='align-items-center justify-content-center text-center'>
               <Col md={10} lg={8}>
                  <img
                     src={logoImage}
                     alt='App Logo'
                     className='hero-image mb-3'
                  />
                  <h1 className='display-4'>{t("home.title")}</h1>
                  <p>{t("home.description")}</p>
                  <p>{t("home.additionalText")}</p>
                  <div className='cta-buttons'>
                    <div className='store-group' ref={groupRef}>
                      <div className='store-row' ref={rowRef}>
                        <a
                          href='https://play.google.com/store/apps/details?id=com.OctopodSystems.CheckChecker'
                          target='_blank'
                          rel='noreferrer noopener'
                          className='btn btn-google'
                        >
                          <img src={googleBadgeSrc} alt='Google Play badge' className='store-badge' onError={onGoogleBadgeError} />
                        </a>
                        <a
                          href='https://apps.apple.com/app/checkchecker-by-octopodsystems/id6744112493'
                          target='_blank'
                          rel='noreferrer noopener'
                          className='btn btn-apple'
                        >
                          <img src={appleBadgeSrc} alt='App Store badge' className='store-badge' onError={onAppleBadgeError} />
                        </a>
                      </div>
                      <a
                        href='https://app.checkchecker.app/app/signin'
                        target='_blank'
                        rel='noreferrer noopener'
                        className='btn btn-primary'
                      >
                        <img src={webLogo} alt='Web App' className='btn-icon-image' />
                        <span className='btn-text'>
                          {t("webPromo.cta")}
                        </span>
                      </a>
                    </div>
                  </div>
               </Col>
            </Row>
         </Container>
      </motion.div>
   );
};

export default Home;
